import React, { useEffect, useState } from 'react';
import { arrayOf, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import { useIntl } from '../../../../util/reactIntl';

import SectionContainer from '../SectionContainer';
import css from './SectionCarousel.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  {
    css: css.oneColumn,
    responsiveImageSizes: '(max-width: 767px) 100vw, 1200px',
  },
  {
    css: css.twoColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, 600px',
  },
  {
    css: css.threeColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, 400px',
  },
  {
    css: css.fourColumns,
    responsiveImageSizes: '(max-width: 767px) 100vw, 290px',
  },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config
    ? config.responsiveImageSizes
    : COLUMN_CONFIG[0].responsiveImageSizes;
};

// Section component that's able to show blocks in a carousel
// the number blocks visible is defined by "numColumns" prop.
const SectionCarousel = props => {
  const intl = useIntl();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    options,
  } = props;
  const sliderContainerId = `${props.sectionId}-container`;
  const sliderId = `${props.sectionId}-slider`;
  const numberOfBlocks = blocks?.length;
  const hasBlocks = numberOfBlocks > 0;
  const project = options?.projects;
  const onToggleFavorite = options?.onToggleFavorite;
  const isAuthenticated = options?.isAuthenticated;
  // Scroll snapping is switched on only once the page has loaded. While the page
  // is still loading, a mandatory snap container keeps re-snapping as its
  // contents change size, and Chrome treats every one of those adjustments as a
  // scroll -- which permanently stops Largest Contentful Paint from being
  // recorded. See the .snapEnabled comment in SectionCarousel.module.css.
  const [snapEnabled, setSnapEnabled] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    // On client-side navigation the document is already loaded, so enable on the
    // next frame instead of waiting for a 'load' event that will never fire.
    if (document.readyState === 'complete') {
      const rafId = window.requestAnimationFrame(() => setSnapEnabled(true));
      return () => window.cancelAnimationFrame(rafId);
    }
    const onLoad = () => setSnapEnabled(true);
    window.addEventListener('load', onLoad);
    return () => window.removeEventListener('load', onLoad);
  }, []);

  useEffect(() => {
    const setCarouselWidth = () => {
      if (hasBlocks) {
        const windowWidth = window.innerWidth;
        const elem = window.document.getElementById(sliderContainerId);
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const elementWidth =
          elem.clientWidth >= windowWidth - scrollbarWidth
            ? windowWidth
            : elem.clientWidth;
        const carouselWidth = elementWidth - scrollbarWidth;

        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
      }
    };
    // Measure on the next animation frame rather than synchronously inside the mount
    // effect. Reading document.body.clientWidth / elem.clientWidth right after React commits
    // (and then writing the --carouselWidth style) forces a synchronous layout; when several
    // carousels mount together their interleaved read→write→read sequences thrash layout,
    // which PageSpeed reports as "Forced reflow". Deferring to rAF lets the browser lay out
    // once and the reads become cheap. Resize keeps using rAF for the same reason.
    let rafId = window.requestAnimationFrame(setCarouselWidth);
    const onResize = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(setCarouselWidth);
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields(
    [title, description, callToAction],
    fieldOptions
  );

  const onSlideLeft = e => {
    var slider = window.document.getElementById(sliderId);
    const slideWidth = numColumns * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft - slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onSlideRight = e => {
    var slider = window.document.getElementById(sliderId);
    const slideWidth = numColumns * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft + slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideRight(e);
    }
  };

  const isReviewSection = sectionId === 'review-section';

const isFeaturedListing = options?.isFeaturedListing;
  return (
    <SectionContainer
      id={sectionId}
      className={className}
      projects={project}
      onToggleFavorite={onToggleFavorite}
      isAuthenticated={isAuthenticated}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      isFeaturedListing={isFeaturedListing}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field
            data={title}
            className={defaultClasses.title}
            options={fieldOptions}
          />
          <Field
            data={description}
            className={defaultClasses.description}
            options={fieldOptions}
          />
          <Field
            data={callToAction}
            className={defaultClasses.ctaButton}
            options={fieldOptions}
          />
        </header>
      ) : null}
      {hasBlocks ? (
        <div className={css.carouselContainer} id={sliderContainerId}>
          <div
            className={classNames(css.carouselArrows, {
              [css.notEnoughBlocks]: numberOfBlocks <= numColumns,
              [css.reviewArrows]: isReviewSection,
            })}
          >
            <button
              className={css.carouselArrowPrev}
              onClick={onSlideLeft}
              onKeyDown={onKeyDown}
              aria-label={intl.formatMessage({ id: 'SectionCarousel.previousSlide' })}
            >
              {isReviewSection ? (
                <svg
                  width="62"
                  height="63"
                  viewBox="0 0 62 63"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_d_8847_5446)">
                    <circle
                      cx="31.0002"
                      cy="27.2029"
                      r="27"
                      transform="rotate(179.568 31.0002 27.2029)"
                      fill="white"
                    />
                  </g>
                  <path
                    d="M35.1102 18.0647L24.1002 28.6213L35.2682 39.0105"
                    stroke="#712739"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  />
                  <defs>
                    <filter
                      id="filter0_d_8847_5446"
                      x="0"
                      y="0.203055"
                      width="62"
                      height="62"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood flood-opacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="4" />
                      <feGaussianBlur stdDeviation="2" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_8847_5446"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_8847_5446"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              ) : (
                '‹'
              )}
            </button>
            <button
              className={css.carouselArrowNext}
              onClick={onSlideRight}
              onKeyDown={onKeyDown}
              aria-label={intl.formatMessage({ id: 'SectionCarousel.nextSlide' })}
            >
              {isReviewSection ? (
                <svg
                  width="62"
                  height="62"
                  viewBox="0 0 62 62"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_d_8847_5442)">
                    <circle cx="31" cy="27" r="27" fill="white" />
                  </g>
                  <path
                    d="M26.8214 36.1071L37.9106 25.6339L26.8214 15.1606"
                    stroke="#712739"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  />
                  <defs>
                    <filter
                      id="filter0_d_8847_5442"
                      x="0"
                      y="0"
                      width="62"
                      height="62"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood flood-opacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="4" />
                      <feGaussianBlur stdDeviation="2" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_8847_5442"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_8847_5442"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              ) : (
                '›'
              )}
            </button>
          </div>
          <div
            className={classNames(getColumnCSS(numColumns), {
              [css.snapEnabled]: snapEnabled,
            })}
            id={sliderId}
          >
            <BlockBuilder
              rootClassName={css.block}
              ctaButtonClass={defaultClasses.ctaButton}
              blocks={blocks}
              sectionId={sectionId}
              responsiveImageSizes={getResponsiveImageSizes(numColumns)}
              options={options}
            />
          </div>
        </div>
      ) : null}
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionCarousel.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  numColumns: 1,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  options: null,
};

SectionCarousel.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  numColumns: number,
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionCarousel;
